import React, { useMemo, useContext } from 'react'
import { useParams } from 'react-router-dom'
import { useRouteMatch } from 'react-router'
import {
  GetPreviewDetailsQuery,
  useGetPreviewPageQuery,
  useGetPreviewPrototypesQuery,
  GetPreviewPageQuery,
  GetPreviewPrototypesQuery,
} from '@sketch/gql-types'
import { RouteParams, routes } from '@sketch/modules-common'

export interface PreviewContextProps {
  preview: NonNullable<GetPreviewDetailsQuery['preview']>
  currentPage?: GetPreviewPageQuery['preview']['page']
  prototypes?: GetPreviewPrototypesQuery['preview']['prototypes']['entries']
  isLoadingPage: boolean
  hasErrorLoading: boolean
  password?: string
}

export const PreviewContext = React.createContext<
  PreviewContextProps | undefined
>(undefined)

type PreviewContextProviderProps = {
  children: React.ReactNode
  preview: NonNullable<GetPreviewDetailsQuery['preview']>
  password?: string
}

export function PreviewContextProvider({
  children,
  preview,
  password,
}: PreviewContextProviderProps) {
  const { pageUUID } = useParams<RouteParams<'PREVIEW_PAGE_VIEW'>>()

  // Check if we're in the preview prototype player route
  const isPreviewPrototypePlayerRoute = !!useRouteMatch({
    path: routes.PREVIEW_PROTOTYPE_PLAYER.template(),
    exact: true,
  })

  const firstPageUUID = preview.pages.entries[0]?.uuid
  const currentPageUUID = pageUUID || firstPageUUID || undefined

  const { data, loading, error } = useGetPreviewPageQuery({
    variables: {
      previewIdentifier: preview.identifier,
      pageUUID: currentPageUUID!,
      password,
    },
    skip: !currentPageUUID,
  })

  const prototypeData = useGetPreviewPrototypesQuery({
    variables: {
      previewIdentifier: preview.identifier,
      password,
    },
    skip: !isPreviewPrototypePlayerRoute,
  })

  const prototypes = prototypeData.data?.preview.prototypes.entries || undefined
  const currentPage = currentPageUUID && data ? data.preview.page : undefined

  const contextValue = useMemo(() => {
    return {
      preview,
      currentPage,
      prototypes,
      isLoadingPage: loading,
      hasErrorLoading: !!error,
      password,
    }
  }, [preview, currentPage, error, loading, prototypes, password])

  return (
    <PreviewContext.Provider value={contextValue}>
      {children}
    </PreviewContext.Provider>
  )
}

export function usePreview() {
  const contextValue = useContext(PreviewContext)

  return contextValue
}

export function useIsPreviewView() {
  const contextValue = useContext(PreviewContext)

  return Boolean(contextValue)
}
